import cx from 'classnames';
import { noop } from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';

import cypressIds from 'Components/cypress_ids.json';
import type { VehicleListItem } from 'Types/vehicle';
import { applyCypressData } from 'Utilities/helpers';
import ROUTES from 'Utilities/routes';

import styles from '../VehicleCards.module.scss';

type VehicleCardProps = React.PropsWithChildren<{
	className?: string;
	footerButton?: React.ReactNode;
	loading?: boolean;
	loadingView?: React.ReactNode;
	onClick?: () => void;
	sticky?: boolean;
	vehicle?: VehicleListItem;
}>;

const VehicleCard: React.FC<VehicleCardProps> = ({
	children,
	className = '',
	footerButton,
	loading = false,
	loadingView,
	onClick = noop,
	sticky = false,
	vehicle = {} as VehicleListItem,
}) => {
	const router = useRouter();
	const baseClassName = cx(styles.vehicleCardContainer, className);
	const { id } = vehicle;

	if (loading) {
		return <div className={cx(styles.vehicleCardLoading, baseClassName)}>{loadingView}</div>;
	}

	const onClickHandler = (e: React.MouseEvent<HTMLAnchorElement>) => {
		onClick();

		if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) {
			return;
		}

		// https://developer.mozilla.org/en-US/docs/Web/API/Event/defaultPrevented
		if (!e.defaultPrevented) {
			void router.push(
				`${ROUTES.VEHICLE.href}?vehicleId=${id}&listScrollPosition=${window.scrollY}`,
				ROUTES.VEHICLE.as(id),
			);
		}

		e.preventDefault();
	};

	return (
		<div
			className={cx(baseClassName, {
				[styles.withFooterButton]: !!footerButton,
				[styles.sticky]: sticky,
			})}
			{...applyCypressData(cypressIds.lists.vehicle)}
		>
			<Link
				as={ROUTES.VEHICLE.as(id)}
				href={`${ROUTES.VEHICLE.href}?vehicleId=${id}`}
				{...applyCypressData(cypressIds.links.vehicleCard)}
				className={cx('mw-i', styles.vehicleCard)}
				id={`vehicle_card_${id}`}
				onClick={onClickHandler}
			>
				{children}
			</Link>
			{footerButton && <div className={styles.vehicleCardCta}>{footerButton}</div>}
		</div>
	);
};

export default VehicleCard;
